/**
 * DocumentAiModal — „Beleg prüfen".
 *
 * Ein Weg für alles: Öffnen, Beleg lesen (automatisch, wenn noch keine
 * Analyse vorliegt), drei Werte prüfen oder korrigieren, speichern.
 *
 * Aufbau: links der Beleg mit einer Werkzeugzeile (drehen, groß öffnen),
 * rechts ein ruhiges Formular ohne Kästen — Beschreibung, Zustandszeile,
 * Betrag/Datum/Name, eine Zeile Sekundärinfos mit aufklappbaren Details.
 *
 * Bewusst weggelassen gegenüber der Vorgängerversion: Übernehmen/Verwerfen je
 * Feld, Sicherheitsbalken, farbige Meldungsflächen, Verbrauchsblock. Das Feld
 * IST der Vorschlag; gespeichert wird, was drinsteht.
 *
 * Datenquellen: POST /api/documents/ask-ai (liest), PUT /api/documents/:id
 * (speichert), POST /api/documents/rotate/:id (dreht).
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowsPointingOutIcon,
  ChevronDownIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import { AuthContext } from '../../App';
import { ModalShell } from './core';
import { Button } from '../Button';
import { ANALYSE_TIMEOUT_MS } from '../../hooks/useDocumentManagement';
import PdfVorschau from '../PdfVorschau';
import { parseDocumentAmount, documentDateToIso, formatDocumentAmount, formatDocumentDate } from '../../utils/documentFields';
import { useDocumentSession } from '../../utils/useDocumentSession';

// ---------------------------------------------------------------------------
// Typen
// ---------------------------------------------------------------------------

type FieldKey = 'amount' | 'date' | 'name';

interface FieldState {
  value: string;
  /** Selbsteinschätzung der KI in Prozent; null ohne Angabe. */
  confidence: number | null;
  /** Vom Nutzer im Feld geändert — dann zählt die KI-Einschätzung nicht mehr. */
  geaendert: boolean;
}

/** Drehung im Uhrzeigersinn, wie die Rotate-Route sie erwartet. */
type Drehung = 90 | 180 | 270;

/** Was die Analyse außer den drei Kernwerten geliefert hat. */
interface Analyse {
  category: string | null;
  categoryConfidence: number | null;
  documentType: string | null;
  issuer: string | null;
  issuerShort: string | null;
  tags: string[];
  details: Record<string, unknown>;
  model: string | null;
  scannedAt: string | null;
  fileName: string | null;
  /** Dauer und Kosten dieses Laufs; nur direkt nach einer Analyse bekannt. */
  dauerMs: number | null;
  kostenEur: number | null;
}

type Phase = 'ruhe' | 'laeuft' | 'fehler' | 'timeout';

export interface DocumentAiModalProps {
  open: boolean;
  onClose: () => void;
  doc: any | null;
  /** Nach dem Speichern: Liste neu laden. */
  onSaved?: () => void;
  /** Nach einer Änderung an der Datei (Drehung): Thumbnail/Detail nachladen. */
  onDocumentChanged?: (docId: number) => void;
  /** „Speichern & buchen": öffnet danach das Buchungsmodal. */
  onBook?: (doc: any) => void;
}

// ---------------------------------------------------------------------------
// Helfer
// ---------------------------------------------------------------------------

/**
 * Betrag aus Freitext lesen. Deutsch ("1.049,88") ist Standard; ein einzelner
 * Punkt mit höchstens zwei Nachstellen ("1049.88") ist trotzdem ein
 * Dezimaltrenner, sonst würde daraus 104988.
 */
export const parseAmount = parseDocumentAmount;
const formatDate = formatDocumentDate;
const formatAmountInput = formatDocumentAmount;

const formatEuro = (value: unknown): string | null => {
  const n = parseFloat(String(value ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || n === 0) return null;
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

/** ai_confidence kommt je nach Treiber als JSON-Objekt oder als String. */
const leseConfidence = (roh: unknown): Record<string, number> | null => {
  if (!roh) return null;
  if (typeof roh === 'string') {
    try {
      return JSON.parse(roh);
    } catch {
      return null;
    }
  }
  return roh as Record<string, number>;
};

const UNSICHER_AB = 75;

const DETAIL_DEFS: { key: string; label: string; art?: 'betrag' | 'datum' | 'prozent' }[] = [
  { key: 'invoice_number', label: 'Rechnungsnummer' },
  { key: 'contract_number', label: 'Vertragsnummer' },
  { key: 'recipient', label: 'Empfänger' },
  { key: 'net_amount', label: 'Netto', art: 'betrag' },
  { key: 'tax_rate', label: 'Steuersatz', art: 'prozent' },
  { key: 'tax_amount', label: 'Steuerbetrag', art: 'betrag' },
  { key: 'currency', label: 'Währung' },
  { key: 'due_date', label: 'Fällig am', art: 'datum' },
  { key: 'payment_status', label: 'Zahlungsstatus' },
  { key: 'payment_method', label: 'Zahlungsart' },
  { key: 'payment_date', label: 'Bezahlt am', art: 'datum' },
  { key: 'contract_start_date', label: 'Vertrag ab', art: 'datum' },
  { key: 'contract_end_date', label: 'Vertrag bis', art: 'datum' },
  { key: 'cancellation_period', label: 'Kündigungsfrist (Tage)' },
];

const formatDetail = (wert: unknown, art?: 'betrag' | 'datum' | 'prozent'): string | null => {
  if (wert === null || wert === undefined || wert === '') return null;
  if (art === 'betrag') return formatEuro(wert);
  if (art === 'datum') return formatDate(wert) || null;
  if (art === 'prozent') return `${parseFloat(String(wert))} %`;
  return String(wert);
};

const hatBuchung = (doc: any): boolean =>
  !!doc && (doc.is_linked === 1 || (Array.isArray(doc.linked_bookings) && doc.linked_bookings.length > 0));

/** Typografie-Stufen (Visual-Konzept): B = Wert, C = Label, D = Meta. */
const T = {
  wert: 'text-[14px] text-gray-900 dark:text-white',
  label: 'text-[13px] font-semibold text-gray-700 dark:text-gray-200',
  meta: 'text-[12px] text-gray-500 dark:text-gray-400',
  textButton:
    'text-[12px] font-semibold text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white',
};

const INPUT_BASE =
  'h-10 w-full rounded-lg border bg-white px-3 text-[14px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:bg-gray-800 dark:text-white';

// ---------------------------------------------------------------------------
// Komponente
// ---------------------------------------------------------------------------

const DocumentAiModal: React.FC<DocumentAiModalProps> = ({
  open,
  onClose,
  doc,
  onSaved,
  onDocumentChanged,
  onBook,
}) => {
  const { BASE_URL } = useContext(AuthContext);
  const isCurrentSession = useDocumentSession(`${open}:${doc?.id ?? ''}`);
  const analysisRequest = useRef(0);
  const saveInFlight = useRef(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const [fields, setFields] = useState<Record<FieldKey, FieldState> | null>(null);
  const [summary, setSummary] = useState('');
  const [analyse, setAnalyse] = useState<Analyse | null>(null);
  const [phase, setPhase] = useState<Phase>('ruhe');
  const [fehlerText, setFehlerText] = useState<string | null>(null);
  /** Nach 30 s Laufzeit ein zweiter Halbsatz, damit niemand abbricht. */
  const [langsam, setLangsam] = useState(false);
  /** Fortschrittsstrich: springt nach dem Start per CSS-Transition auf 90 %. */
  const [fortschritt, setFortschritt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [speicherFehler, setSpeicherFehler] = useState<string | null>(null);
  const [detailsOffen, setDetailsOffen] = useState(false);
  /** Verbuchter Beleg: Betrag und Datum gesperrt, bis der Nutzer es aufhebt. */
  const [sperreAufgehoben, setSperreAufgehoben] = useState(false);
  /** Kosten früherer Analysen aus ai_usage; still nachgeladen. */
  const [frühereKosten, setFrühereKosten] = useState<number | null>(null);

  // Bild und Drehung
  const [drehVorschlag, setDrehVorschlag] = useState<Drehung | null>(null);
  const [rotating, setRotating] = useState(false);
  /**
   * Cache-Buster für die Vorschau. Zeitstempel als Start, damit die URL nach
   * einem Neuladen der Seite nie mit einer alten identisch ist.
   */
  const [previewVersion, setPreviewVersion] = useState(() => Date.now());
  const [ersteVersion] = useState(previewVersion);
  /** Angezeigtes Bild: bleibt beim Drehen stehen, bis das neue geladen ist. */
  const [shownImageUrl, setShownImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);

  const amountRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /** Verhindert, dass der Auto-Start bei jedem Re-Render erneut feuert. */
  const autoGestartetFuer = useRef<number | null>(null);

  const gebucht = hatBuchung(doc);
  const gesperrt = gebucht && !sperreAufgehoben;
  const istPdf = /\.pdf$/i.test(doc?.file_path || '');
  const istBild = /\.(jpe?g|png|webp)$/i.test(doc?.file_path || '');

  // -------------------------------------------------------------------------
  // Zustand aus dem Dokument aufbauen
  // -------------------------------------------------------------------------

  const feldAusWert = (value: string, confidence: number | null): FieldState => ({
    value,
    confidence,
    geaendert: false,
  });

  /** Felder und Analyse aus einer Dokumentzeile (Liste oder GET /:id). */
  const ausDokument = useCallback((d: any) => {
    const conf = leseConfidence(d?.ai_confidence);
    const analysiert = !!d?.scanned_at || !!d?.ai_comment;
    const felder: Record<FieldKey, FieldState> = {
      amount: feldAusWert(formatAmountInput(d?.invoice_amount), analysiert ? conf?.invoice_amount ?? null : null),
      date: feldAusWert(formatDate(d?.invoice_date), analysiert ? conf?.invoice_date ?? null : null),
      name: feldAusWert(d?.name || '', null),
    };
    const tags = String(d?.tag || '')
      .split(',')
      .map((t: string) => t.trim())
      .filter(Boolean);
    const gespeichert: Analyse | null = analysiert
      ? {
          category: tags[0] || null,
          categoryConfidence: conf?.category ?? null,
          documentType: d?.document_type ?? null,
          issuer: d?.issuer ?? null,
          issuerShort: d?.issuer_short ?? null,
          tags,
          details: {
            invoice_number: d?.invoice_number,
            contract_number: d?.contract_number,
            recipient: d?.recipient,
            net_amount: d?.net_amount,
            tax_rate: d?.tax_rate,
            tax_amount: d?.tax_amount,
            currency: d?.currency,
            due_date: d?.due_date,
            payment_status: d?.payment_status,
            payment_method: d?.payment_method,
            payment_date: d?.payment_date,
            contract_start_date: d?.contract_start_date,
            contract_end_date: d?.contract_end_date,
            cancellation_period: d?.cancellation_period,
          },
          model: d?.ai_model ?? null,
          scannedAt: d?.scanned_at ?? null,
          fileName: d?.file_path ?? null,
          dauerMs: null,
          kostenEur: null,
        }
      : null;
    return { felder, gespeichert, beschreibung: d?.ai_comment || '' };
  }, []);

  // -------------------------------------------------------------------------
  // Analyse
  // -------------------------------------------------------------------------

  const runAnalysis = useCallback(async () => {
    if (!doc || !open) return;
    const request = ++analysisRequest.current;
    const active = () => isCurrentSession() && request === analysisRequest.current;
    setPhase('laeuft');
    setFehlerText(null);
    setSpeicherFehler(null);
    setLangsam(false);
    setFortschritt(0);
    // Erst im nächsten Frame auf 90 %, damit die CSS-Transition greift.
    requestAnimationFrame(() => { if (active()) setFortschritt(90); });
    const langsamTimer = window.setTimeout(() => { if (active()) setLangsam(true); }, 30000);

    try {
      const res = await apiClient.post(
        '/api/documents/ask-ai',
        { document_id: doc.id },
        { timeout: ANALYSE_TIMEOUT_MS },
      );
      if (!active()) return;
      if (!res.data?.success) {
        setFehlerText(res.data?.error || 'Unbekannter Fehler');
        setPhase('fehler');
        return;
      }
      const r = res.data;
      setSummary(r.description || '');
      setFields({
        amount: feldAusWert(formatAmountInput(r.invoice_amount), r.confidence?.invoice_amount ?? null),
        date: feldAusWert(formatDate(r.invoice_date), r.confidence?.invoice_date ?? null),
        // Der Name entsteht aus Aussteller, Datum und Betrag — er ist so
        // verlässlich wie sein schwächster Baustein.
        name: feldAusWert(
          r.suggestedName || doc.name || '',
          Math.min(
            r.confidence?.issuer ?? 100,
            r.confidence?.invoice_date ?? 100,
            r.confidence?.invoice_amount ?? 100,
          ),
        ),
      });
      setAnalyse({
        category: r.category ?? null,
        categoryConfidence: r.confidence?.category ?? null,
        documentType: r.document_type ?? null,
        issuer: r.issuer ?? null,
        issuerShort: r.issuer_short ?? null,
        tags: Array.isArray(r.tags) ? r.tags : [],
        details: r.details || {},
        model: r.ai_model ?? null,
        scannedAt: new Date().toISOString(),
        fileName: r.file_path ?? doc.file_path ?? null,
        dauerMs: r.verbrauch?.dauer_ms ?? null,
        kostenEur: r.verbrauch?.kosten?.eur ?? null,
      });
      const grad = Number(r.rotation);
      setDrehVorschlag(istBild && (grad === 90 || grad === 180 || grad === 270) ? (grad as Drehung) : null);
      setPhase('ruhe');
    } catch (e: any) {
      if (!active()) return;
      console.error('DocumentAiModal: Analyse fehlgeschlagen', e);
      if (e?.code === 'ECONNABORTED') {
        setPhase('timeout');
      } else {
        setFehlerText(e?.response?.data?.error || e?.message || 'Verbindung fehlgeschlagen');
        setPhase('fehler');
      }
    } finally {
      window.clearTimeout(langsamTimer);
    }
  }, [doc, istBild, open, isCurrentSession]);

  /** Nach einem Timeout: Dokumentstand holen, ohne neu zu analysieren. */
  const neuLaden = async () => {
    if (!doc) return;
    try {
      const res = await apiClient.get(`/api/documents/${doc.id}`);
      if (!isCurrentSession()) return;
      const { felder, gespeichert, beschreibung } = ausDokument(res.data);
      setFields(felder);
      setAnalyse(gespeichert);
      setSummary(beschreibung);
      setPhase('ruhe');
    } catch {
      if (!isCurrentSession()) return;
      setFehlerText('Der Beleg konnte nicht neu geladen werden.');
      setPhase('fehler');
    }
  };

  // Öffnen: Zustand aus dem Dokument, ggf. Auto-Start.
  useEffect(() => {
    setSaving(false);
    saveInFlight.current = false;
    setRotating(false);
    setFieldErrors({});
    if (!open || !doc) {
      if (!open) {
        setFields(null);
        setAnalyse(null);
        setSummary('');
        setPhase('ruhe');
        setFehlerText(null);
        setSpeicherFehler(null);
        setDetailsOffen(false);
        setDrehVorschlag(null);
        setSperreAufgehoben(false);
        setFrühereKosten(null);
        autoGestartetFuer.current = null;
      }
      return;
    }
    const { felder, gespeichert, beschreibung } = ausDokument(doc);
    setFields(felder);
    setAnalyse(gespeichert);
    setSummary(beschreibung);
    setDetailsOffen(false);
    setSperreAufgehoben(false);
    setDrehVorschlag(null);
    setSpeicherFehler(null);

    if (gespeichert) {
      apiClient
        .get(`/api/ai-usage/dokument/${doc.id}`)
        .then(r => {
          if (!isCurrentSession()) return;
          const a = r.data?.analysen || [];
          if (a.length) setFrühereKosten(a.reduce((s: number, x: any) => s + (Number(x.kosten_eur) || 0), 0));
        })
        .catch(() => {/* Kosten sind Beiwerk */});
    } else if (!hatBuchung(doc) && autoGestartetFuer.current !== doc.id) {
      // Noch nie gelesen: sofort lesen. Jede Analyse kostet, deshalb nur
      // einmal je geöffnetem Dokument und nie bei verbuchten Belegen.
      autoGestartetFuer.current = doc.id;
      runAnalysis();
    }
  }, [open, doc, ausDokument, runAnalysis, isCurrentSession]);

  // Nach einem Ergebnis: Fokus ins erste unsichere Feld.
  useEffect(() => {
    if (phase !== 'ruhe' || !fields || !analyse) return;
    const unsicher = (f: FieldState) => !f.value || (f.confidence !== null && f.confidence < UNSICHER_AB);
    const ziel = unsicher(fields.amount) ? amountRef : unsicher(fields.date) ? dateRef : unsicher(fields.name) ? nameRef : null;
    if (ziel?.current && !gesperrt) ziel.current.focus();
    // Nur beim Übergang in den Ruhezustand nach einer Analyse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // -------------------------------------------------------------------------
  // Vorschau und Drehen
  // -------------------------------------------------------------------------

  const previewUrl = useMemo(() => {
    if (!doc?.id) return null;
    const base = (
      BASE_URL ||
      localStorage.getItem('club_url') ||
      process.env.REACT_APP_API_URL ||
      'http://localhost:4000'
    ).replace(/\/$/, '');
    const token = localStorage.getItem('jwt_token') || '';
    return `${base}/api/documents/download-url/${doc.id}?token=${encodeURIComponent(token)}&v=${previewVersion}`;
  }, [doc, BASE_URL, previewVersion]);

  // Neues Bild im Hintergrund laden, erst dann austauschen.
  useEffect(() => {
    if (!previewUrl || istPdf) {
      setShownImageUrl(null);
      return;
    }
    if (previewVersion === ersteVersion) {
      setShownImageUrl(previewUrl);
      return;
    }
    let aktiv = true;
    setImageLoading(true);
    const img = new Image();
    img.onload = img.onerror = () => {
      if (!aktiv) return;
      setShownImageUrl(previewUrl);
      setImageLoading(false);
    };
    img.src = previewUrl;
    return () => {
      aktiv = false;
    };
  }, [previewUrl, previewVersion, ersteVersion, istPdf]);

  const rotate = async (degrees: Drehung) => {
    if (!doc || rotating) return;
    setRotating(true);
    setSpeicherFehler(null);
    try {
      const res = await apiClient.post(`/api/documents/rotate/${doc.id}`, { degrees }, { timeout: 60000 });
      if (!isCurrentSession()) return;
      if (!res.data?.success) {
        setSpeicherFehler(res.data?.error || 'Das Bild konnte nicht gedreht werden.');
        return;
      }
      setDrehVorschlag(null);
      setPreviewVersion(Date.now());
      onDocumentChanged?.(doc.id);
    } catch (e: any) {
      if (!isCurrentSession()) return;
      setSpeicherFehler(e?.response?.data?.error || 'Das Bild konnte nicht gedreht werden.');
    } finally {
      if (isCurrentSession()) setRotating(false);
    }
  };

  // -------------------------------------------------------------------------
  // Speichern
  // -------------------------------------------------------------------------

  const patch = (key: FieldKey, value: string) => {
    setFieldErrors(prev => ({ ...prev, [key]: undefined }));
    setFields(prev => (prev ? { ...prev, [key]: { ...prev[key], value, geaendert: true } } : prev));
  };

  /** Alles speichern, was einen Wert hat. Liefert true bei Erfolg. */
  const speichern = async (): Promise<boolean> => {
    if (!doc || !fields || saveInFlight.current || !isCurrentSession()) return false;
    const payload: Record<string, unknown> = {};
    if (!gesperrt) {
      const betrag = parseAmount(fields.amount.value);
      const isoDate = documentDateToIso(fields.date.value);
      const errors: Partial<Record<FieldKey, string>> = {};
      if (fields.amount.value.trim() && betrag === null) errors.amount = 'Bitte einen gültigen Betrag eingeben (z. B. 1.234,56).';
      if (fields.date.value.trim() && !isoDate) errors.date = 'Bitte ein gültiges Datum eingeben (tt.mm.jjjj).';
      if (Object.keys(errors).length) { setFieldErrors(errors); return false; }
      payload.invoice_amount = betrag;
      payload.invoice_date = isoDate;
    }
    if (fields.name.value.trim()) payload.name = fields.name.value.trim();
    if (summary) payload.ai_comment = summary;
    if (analyse?.tags.length) payload.tag = analyse.tags.join(',');

    if (Object.keys(payload).length === 0) {
      setSpeicherFehler('Es gibt nichts zu speichern.');
      return false;
    }
    saveInFlight.current = true;
    setSaving(true);
    setSpeicherFehler(null);
    try {
      await apiClient.put(`/api/documents/${doc.id}`, payload);
      if (!isCurrentSession()) return false;
      onSaved?.();
      return true;
    } catch (e) {
      if (!isCurrentSession()) return false;
      console.error('DocumentAiModal: Speichern fehlgeschlagen', e);
      setSpeicherFehler('Die Werte konnten nicht gespeichert werden.');
      return false;
    } finally {
      if (isCurrentSession()) { saveInFlight.current = false; setSaving(false); }
    }
  };

  const speichernUndSchliessen = async () => {
    if (await speichern() && isCurrentSession()) onClose();
  };

  const speichernUndBuchen = async () => {
    if (!doc || !(await speichern())) return;
    const betrag = parseAmount(fields?.amount.value || '');
    onBook?.({ ...doc, invoice_amount: betrag ?? doc.invoice_amount, invoice_date: documentDateToIso(fields?.date.value), name: fields?.name.value || doc.name });
    onClose();
  };

  // -------------------------------------------------------------------------
  // Abgeleitete Anzeige
  // -------------------------------------------------------------------------

  const laeuft = phase === 'laeuft';

  const sekundaerZeile = analyse
    ? [
        analyse.issuerShort || analyse.issuer,
        analyse.documentType,
        analyse.category,
        analyse.tags.find(t => /^\d{4}$/.test(t)),
      ].filter(Boolean)
    : [];

  const detailZeilen = useMemo(() => {
    if (!analyse) return [];
    const zeilen = DETAIL_DEFS.map(d => ({ label: d.label, wert: formatDetail(analyse.details[d.key], d.art) })).filter(
      z => z.wert !== null,
    ) as { label: string; wert: string }[];
    if (analyse.fileName) zeilen.push({ label: 'Dateiname', wert: analyse.fileName });
    if (analyse.model) zeilen.push({ label: 'Modell', wert: analyse.model });
    if (analyse.scannedAt) zeilen.push({ label: 'Analysiert am', wert: formatDate(analyse.scannedAt) });
    const kosten = analyse.kostenEur ?? frühereKosten;
    if (kosten !== null && kosten !== undefined) {
      zeilen.push({ label: 'Kosten', wert: `${kosten.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €` });
    }
    return zeilen;
  }, [analyse, frühereKosten]);

  const fussnote = analyse
    ? [
        analyse.scannedAt ? `Analysiert am ${formatDate(analyse.scannedAt)}` : null,
        analyse.dauerMs !== null ? `${(analyse.dauerMs / 1000).toFixed(0)} s` : null,
        (analyse.kostenEur ?? frühereKosten) !== null && (analyse.kostenEur ?? frühereKosten) !== undefined
          ? `${(analyse.kostenEur ?? frühereKosten)!.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
          : null,
      ].filter(Boolean)
    : [];

  /** Statusangabe rechts vom Label: Punkt + kurzer Text. */
  const feldStatus = (f: FieldState) => {
    if (f.geaendert) return { punkt: null as string | null, text: 'geändert', unsicher: false };
    if (!f.value) return { punkt: null, text: analyse ? 'nicht erkannt' : '', unsicher: !!analyse };
    if (f.confidence === null) return { punkt: null, text: '', unsicher: false };
    if (f.confidence < UNSICHER_AB) {
      return { punkt: 'bg-warning-500 dark:bg-warning-400', text: `bitte prüfen · ${f.confidence} %`, unsicher: true };
    }
    return { punkt: 'bg-success-500 dark:bg-success-400', text: 'von der KI', unsicher: false };
  };

  const FELDER: { key: FieldKey; label: string; ref: React.RefObject<HTMLInputElement | null>; inputMode?: 'decimal' | 'text'; placeholder: string }[] = [
    { key: 'amount', label: 'Betrag', ref: amountRef, inputMode: 'decimal', placeholder: '0,00' },
    { key: 'date', label: 'Belegdatum', ref: dateRef, inputMode: 'text', placeholder: 'tt.mm.jjjj' },
    { key: 'name', label: 'Name', ref: nameRef, inputMode: 'text', placeholder: 'Dokumentname' },
  ];

  const iconButton =
    'inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white';

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const footer = (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-3">
      <button
        type="button"
        onClick={runAnalysis}
        disabled={laeuft || saving || rotating}
        className={`${T.textButton} inline-flex min-h-[44px] items-center gap-1.5 disabled:opacity-40 sm:min-h-0`}
      >
        <ArrowPathIcon className={`h-4 w-4 ${laeuft ? 'animate-spin' : ''}`} aria-hidden="true" />
        {analyse || summary ? 'Erneut analysieren' : 'Analyse starten'}
      </button>
      <div className="flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row sm:items-center sm:gap-3">
        <Button variant="tertiary" onClick={onClose} className="min-h-[44px] w-full justify-center sm:w-auto">
          Abbrechen
        </Button>
        {onBook && !gebucht && (
          <Button
            variant="tertiary"
            onClick={speichernUndBuchen}
            disabled={laeuft || saving || !fields}
            className="min-h-[44px] w-full justify-center sm:w-auto"
          >
            Speichern &amp; buchen
          </Button>
        )}
        <Button
          variant="primary"
          onClick={speichernUndSchliessen}
          disabled={laeuft || !fields}
          loading={saving}
          className="min-h-[44px] w-full justify-center sm:w-auto"
        >
          Speichern
        </Button>
      </div>
    </div>
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="form"
      size="xl"
      icon={SparklesIcon}
      title="Beleg prüfen"
      subtitle={doc?.name}
      footer={footer}
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
        {/* ------------------------------------------------------------- */}
        {/* Links: Beleg                                                    */}
        {/* ------------------------------------------------------------- */}
        <div className="flex w-full flex-col gap-2 sm:w-[360px] sm:shrink-0">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
            {!previewUrl ? (
              <div className={`flex h-48 items-center justify-center ${T.meta}`}>Keine Vorschau</div>
            ) : istPdf ? (
              // Gerenderte Seiten statt iframe: wie die PDF-Ansicht in der Hundeverwaltung.
              <PdfVorschau
                quelle={`/api/documents/download/${doc.id}`}
                hoeheKlasse="h-[240px] sm:h-[460px]"
                version={previewVersion}
              />
            ) : (
              <div className="relative">
                <img
                  src={shownImageUrl ?? previewUrl}
                  alt={`Vorschau ${doc?.name || ''}`}
                  className={`max-h-[240px] w-full bg-white object-contain transition-opacity sm:max-h-[460px] ${
                    rotating || imageLoading ? 'opacity-60' : ''
                  }`}
                />
                {rotating && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-lg bg-gray-900/80 px-3 py-1.5 text-[12px] font-semibold text-white">
                      Bild wird gedreht …
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Schieflage laut KI: eine Zeile, eine Aktion. */}
          {drehVorschlag && istBild && (
            <div className="flex items-center gap-2 px-1">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-warning-500 dark:bg-warning-400" aria-hidden="true" />
              <span className="text-[12px] text-warning-700 dark:text-warning-400">
                {drehVorschlag === 180 ? 'Beleg steht auf dem Kopf' : 'Beleg liegt quer'}
              </span>
              <button
                type="button"
                onClick={() => rotate(drehVorschlag)}
                disabled={rotating || laeuft || saving}
                className="ml-auto text-[12px] font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-40 dark:text-primary-400"
              >
                Drehen
              </button>
            </div>
          )}

          {/* Werkzeugzeile: nur Icons */}
          <div className="flex items-center gap-1">
            {istBild && (
              <>
                <button type="button" onClick={() => rotate(270)} disabled={rotating || laeuft || saving} title="90° nach links drehen" className={iconButton}>
                  <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => rotate(90)} disabled={rotating || laeuft || saving} title="90° nach rechts drehen" className={iconButton}>
                  <ArrowUturnRightIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => rotate(180)} disabled={rotating || laeuft || saving} title="Um 180° drehen" className={iconButton}>
                  <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </>
            )}
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Groß öffnen"
                className={`${iconButton} ml-auto`}
              >
                <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
              </a>
            )}
          </div>
        </div>

        {/* ------------------------------------------------------------- */}
        {/* Rechts: Formular                                                */}
        {/* ------------------------------------------------------------- */}
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          {/* Beschreibung */}
          <p className={`${T.wert} leading-relaxed ${summary ? '' : 'text-gray-500 dark:text-gray-400'}`}>
            {summary || (laeuft ? 'Der Beleg wird gerade gelesen.' : 'Für diesen Beleg liegt noch keine Analyse vor.')}
          </p>

          {/* Zustandszeile */}
          {laeuft && (
            <div className="flex flex-col gap-2">
              <p className={T.meta}>
                Beleg wird gelesen … meist 20 bis 60 Sekunden{langsam ? ' — bitte noch einen Moment' : ''}
              </p>
              <div className="h-0.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full bg-primary-500 transition-[width] duration-[45000ms] ease-out"
                  style={{ width: `${fortschritt}%` }}
                />
              </div>
            </div>
          )}
          {phase === 'fehler' && (
            <p className="text-[12px] text-error-600 dark:text-error-400">
              Analyse fehlgeschlagen: {fehlerText}
              <button type="button" onClick={runAnalysis} className="ml-2 font-semibold underline-offset-2 hover:underline">
                Erneut versuchen
              </button>
            </p>
          )}
          {phase === 'timeout' && (
            <p className={T.meta}>
              Die Analyse dauert länger als üblich. Das Ergebnis wird am Beleg gespeichert.
              <button type="button" onClick={neuLaden} className={`${T.textButton} ml-2`}>
                Neu laden
              </button>
            </p>
          )}
          {gebucht && phase === 'ruhe' && (
            <p className={T.meta}>
              Mit einer Buchung verknüpft — Betrag und Datum sind {gesperrt ? 'gesperrt' : 'freigegeben'}.
              {gesperrt && (
                <button type="button" onClick={() => setSperreAufgehoben(true)} className={`${T.textButton} ml-2`}>
                  Trotzdem ändern
                </button>
              )}
            </p>
          )}

          {/* Felder */}
          <div className="flex flex-col gap-4">
            {FELDER.map(def => {
              const f = fields?.[def.key];
              const status = f ? feldStatus(f) : { punkt: null, text: '', unsicher: false };
              const gesperrtesFeld = gesperrt && def.key !== 'name';
              return (
                <div key={def.key}>
                  <div className="mb-1.5 flex items-baseline justify-between gap-3">
                    <label htmlFor={`ki-${def.key}`} className={T.label}>
                      {def.label}
                    </label>
                    {!laeuft && status.text && (
                      <span
                        className={`inline-flex items-center gap-1.5 text-[12px] ${
                          status.unsicher ? 'text-warning-700 dark:text-warning-400' : 'text-gray-500 dark:text-gray-400'
                        }`}
                        title={f?.confidence !== null && f?.confidence !== undefined ? `Sicherheit der KI: ${f.confidence} %` : undefined}
                      >
                        {status.punkt && <span className={`h-1.5 w-1.5 rounded-full ${status.punkt}`} aria-hidden="true" />}
                        {status.text}
                      </span>
                    )}
                  </div>
                  {laeuft || !f ? (
                    <div className="h-10 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" aria-hidden="true" />
                  ) : (
                    <div className="relative">
                      <input
                        id={`ki-${def.key}`}
                        ref={def.ref}
                        value={f.value}
                        onChange={e => patch(def.key, e.target.value)}
                        inputMode={def.inputMode}
                        placeholder={def.placeholder}
                        disabled={gesperrtesFeld || saving}
                        aria-invalid={!!fieldErrors[def.key]}
                        aria-describedby={fieldErrors[def.key] ? `ki-${def.key}-error` : undefined}
                        className={`${INPUT_BASE} ${
                          def.key === 'amount' ? 'pr-8 text-right font-semibold tabular-nums' : ''
                        } ${
                          status.unsicher
                            ? 'border-warning-400 dark:border-warning-500/70'
                            : 'border-gray-300 dark:border-gray-600'
                        } disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-gray-800/60`}
                      />
                      {def.key === 'amount' && (
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[14px] text-gray-400" aria-hidden="true">
                          €
                        </span>
                      )}
                    </div>
                  )}
                  {fieldErrors[def.key] && <p id={`ki-${def.key}-error`} role="alert" className="mt-1 text-xs text-error-600 dark:text-error-400">{fieldErrors[def.key]}</p>}
                </div>
              );
            })}
          </div>

          {speicherFehler && <p className="text-[12px] text-error-600 dark:text-error-400">{speicherFehler}</p>}

          {/* Sekundärinfos + Details */}
          {analyse && !laeuft && (
            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <p className={`${T.meta} min-w-0 flex-1 truncate`}>
                  {sekundaerZeile.map((s, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && ' · '}
                      {s === analyse.category && analyse.categoryConfidence !== null && analyse.categoryConfidence < 50 ? (
                        <span className="inline-flex items-center gap-1" title="Rubrik unsicher — bitte selbst einordnen">
                          <span className="h-1.5 w-1.5 rounded-full bg-warning-500 dark:bg-warning-400" aria-hidden="true" />
                          {s}
                        </span>
                      ) : (
                        s
                      )}
                    </React.Fragment>
                  ))}
                  {sekundaerZeile.length === 0 && 'Keine weiteren Angaben'}
                </p>
                {detailZeilen.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setDetailsOffen(o => !o)}
                    aria-expanded={detailsOffen}
                    className={`${T.textButton} inline-flex min-h-[40px] shrink-0 items-center gap-1`}
                  >
                    Details
                    <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${detailsOffen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                )}
              </div>

              {detailsOffen && (
                <dl className="mt-3 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                  {detailZeilen.map(z => (
                    <div
                      key={z.label}
                      className="flex h-8 items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700/60"
                    >
                      <dt className={`${T.meta} shrink-0`}>{z.label}</dt>
                      <dd className="min-w-0 truncate text-right text-[13px] text-gray-900 dark:text-white" title={z.wert}>
                        {z.wert}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}

          {fussnote.length > 0 && !laeuft && <p className={`${T.meta} mt-auto`}>{fussnote.join(' · ')}</p>}
        </div>
      </div>
    </ModalShell>
  );
};

export default DocumentAiModal;
